import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import InventoryBarcodeScanner from './InventoryBarcodeScanner';
import { normalizeInventoryBarcode, rememberBarcode } from './inventoryScannerModel';
import {
  addReplacementLine,
  buildRestockInventoryUpdates,
  buildRestockReconciliationAudit,
  buildRestockReconciliationDraft,
  summarizeReconciliationGroup,
  validateRestockReconciliation,
} from './restockReconciliationModel';
import './restock-reconcile.css';

function formatDate(value) {
  if (!value) return 'No expiration recorded';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) return value;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function requestCode(request) {
  return request?.request_code || request?.id || 'Restock request';
}

function newReplacementDraft(barcode = '') {
  return { barcode, quantity: '1', expirationDate: '', noExpiration: false, lotSerial: '' };
}

async function markReconciledAssetsReady(assetIds) {
  const ids = new Set((assetIds || []).map(id => String(id || '').trim().toUpperCase()).filter(Boolean));
  if (!ids.size) return { ready: [], skipped: [] };

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error('Member sign-in is required to return reconciled bags to ready status.');

  const response = await fetch('/api/member-tracking', {
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}` },
  });
  const state = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(state?.error || 'Unable to load gear status for the reconciled bags.');

  const ready = [];
  const skipped = [];
  const timestamp = new Date().toISOString();
  const assets = (Array.isArray(state.assets) ? state.assets : []).map(asset => {
    const id = String(asset?.id || '').trim().toUpperCase();
    if (!ids.has(id)) return asset;
    if (asset.currentDeploymentId) {
      skipped.push({ id, reason: 'still assigned to a deployment' });
      return asset;
    }
    if (asset.status === 'damaged' || asset.condition === 'damaged') {
      skipped.push({ id, reason: 'still marked damaged' });
      return asset;
    }
    if (asset.status === 'available' && asset.condition === 'ready') return asset;
    ready.push({ id, name: asset.name || id });
    return { ...asset, status: 'available', condition: 'ready', updatedAt: timestamp };
  });

  if (!ready.length) return { ready, skipped };

  const events = [
    ...ready.map((asset, index) => ({
      id: `EVT-${Date.now()}-RESTOCK-${index}-${asset.id}`,
      type: 'asset_ready',
      actor: 'Inventory Admin',
      timestamp,
      assetId: asset.id,
      assetName: asset.name,
      details: 'Inventory reconciled after restock',
    })),
    ...(Array.isArray(state.events) ? state.events : []),
  ];

  const save = await fetch('/api/member-tracking', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ state: { ...state, assets, events } }),
  });
  const payload = await save.json().catch(() => ({}));
  if (!save.ok) throw new Error(payload?.error || 'Inventory was reconciled, but the bag could not be returned to ready status.');
  return { ready, skipped };
}

export default function RestockReconcile({ request, onCancel, onComplete }) {
  const [inventory, setInventory] = useState(null);
  const [groups, setGroups] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [replacementDrafts, setReplacementDrafts] = useState({});
  const [notice, setNotice] = useState('');

  const code = requestCode(request);

  const load = async () => {
    setStatus('loading');
    setError('');
    try {
      const response = await fetch('/api/tracking?admin=inventory', { cache: 'no-store' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to load current bag inventory.');
      setInventory(payload);
      const draft = buildRestockReconciliationDraft(request, payload);
      setGroups(draft);
      setReplacementDrafts(Object.fromEntries(draft.map(group => [group.key, newReplacementDraft()])));
      setStatus('ready');
    } catch (err) {
      setError(err?.message || 'Unable to load current bag inventory.');
      setStatus('error');
    }
  };

  useEffect(() => { load(); }, [request?.id]);

  const validation = useMemo(() => validateRestockReconciliation(groups), [groups]);

  const updateGroup = (key, updater) => {
    setGroups(current => current.map(group => group.key === key ? updater(group) : group));
  };

  const setRemaining = (groupKey, recordId, value) => {
    updateGroup(groupKey, group => ({
      ...group,
      oldLots: group.oldLots.map(lot => lot.recordId === recordId ? { ...lot, remainingQty: value } : lot),
    }));
  };

  const setReplacementDraft = (groupKey, patch) => {
    setReplacementDrafts(current => ({
      ...current,
      [groupKey]: { ...(current[groupKey] || newReplacementDraft()), ...patch },
    }));
  };

  const scanReplacement = (group, raw) => {
    const barcode = normalizeInventoryBarcode(raw);
    if (!barcode) return;
    if (typeof window !== 'undefined') rememberBarcode(window.localStorage, barcode, group.catalogId);
    setReplacementDraft(group.key, { barcode });
    setNotice(`Scanned ${group.item}. Enter the replacement lot / expiration and quantity, then add it to ${group.assetName}.`);
  };

  const addReplacement = group => {
    const draft = replacementDrafts[group.key] || newReplacementDraft();
    const quantity = Number(draft.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) return setError(`${group.item}: enter a replacement quantity greater than zero.`);
    if (!draft.noExpiration && !draft.expirationDate) return setError(`${group.item}: enter the replacement expiration date or select N/A.`);

    const line = addReplacementLine(group, draft);
    updateGroup(group.key, current => ({ ...current, replacements: [...current.replacements, line] }));
    setReplacementDrafts(current => ({ ...current, [group.key]: newReplacementDraft() }));
    setError('');
    setNotice(`${group.item} replacement added to the reconciliation.`);
  };

  const removeReplacement = (groupKey, lineId) => {
    updateGroup(groupKey, group => ({ ...group, replacements: group.replacements.filter(line => line.id !== lineId) }));
  };

  const complete = async () => {
    const check = validateRestockReconciliation(groups);
    if (!check.valid) return setError(check.errors[0] || 'Finish the inventory reconciliation before completing the restock.');

    setStatus('saving');
    setError('');
    setNotice('Saving inventory reconciliation…');
    try {
      const records = buildRestockInventoryUpdates(groups, code);
      const inventoryResponse = await fetch('/api/tracking?admin=inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ records }),
      });
      const inventoryPayload = await inventoryResponse.json().catch(() => ({}));
      if (!inventoryResponse.ok) throw new Error(inventoryPayload?.error || 'Unable to save reconciled inventory.');

      const audit = buildRestockReconciliationAudit(groups);
      const { error: rpcError } = await supabase.rpc('admin_complete_restock_reconciliation', {
        p_request_id: request.id,
        p_reconciliation: audit,
      });
      if (rpcError) throw new Error(rpcError.message || 'Inventory saved, but the restock request could not be marked reconciled.');

      const affectedAssetIds = [...new Set(groups.map(group => group.assetId))];
      let readiness = { ready: [], skipped: [] };
      let readinessWarning = '';
      try {
        readiness = await markReconciledAssetsReady(affectedAssetIds);
      } catch (readyError) {
        readinessWarning = readyError?.message || 'The inventory was reconciled, but gear readiness must be updated manually.';
      }

      setStatus('complete');
      setNotice('');
      onComplete?.({ audit, readiness, warning: readinessWarning });
    } catch (err) {
      setError(err?.message || 'Unable to complete the restock reconciliation.');
      setStatus('ready');
    }
  };

  if (status === 'loading') {
    return <section className="restock-reconcile-shell"><div className="inventory-card">Loading current inventory for {code}…</div></section>;
  }

  if (status === 'error') {
    return (
      <section className="restock-reconcile-shell">
        <div className="inventory-card">
          <h2>Inventory reconciliation unavailable</h2>
          <div className="inventory-error">{error}</div>
          <div className="restock-reconcile-actions"><button type="button" onClick={load}>Try again</button><button type="button" onClick={onCancel}>Back</button></div>
        </div>
      </section>
    );
  }

  return (
    <section className="restock-reconcile-shell">
      <div className="restock-reconcile-head">
        <div>
          <span>{code}</span>
          <h2>Reconcile used supplies</h2>
          <p>Confirm what is still physically in each bag, then scan / enter the replacement product. The app will infer which recorded quantity was consumed.</p>
        </div>
        <button type="button" onClick={onCancel} disabled={status === 'saving'}>Back to requests</button>
      </div>

      {error && <div className="inventory-error restock-reconcile-sticky">{error}</div>}
      {notice && <div className="inventory-scan-success restock-reconcile-sticky">{notice}</div>}

      {groups.map(group => {
        const summary = summarizeReconciliationGroup(group);
        const draft = replacementDrafts[group.key] || newReplacementDraft();
        const groupCheck = validation.checks[groups.indexOf(group)] || { valid: false, errors: [] };
        return (
          <article className={`inventory-card restock-reconcile-card${groupCheck.valid ? ' reconciled' : ''}`} key={group.key}>
            <div className="restock-reconcile-title">
              <div>
                <small>{group.assetName} · {group.assetId}</small>
                <h3>{group.item}</h3>
                <p>{group.component || 'Inventory'} · Reported used: <strong>{group.reportedUsed}</strong> · Standard: <strong>{group.standardQty || 'not listed'}</strong></p>
              </div>
              <strong className={groupCheck.valid ? 'good' : 'needs-work'}>{groupCheck.valid ? 'READY TO SAVE' : 'RECONCILE'}</strong>
            </div>

            <div className="restock-reconcile-metrics">
              <span><small>Recorded before</small><strong>{summary.recordedBefore}</strong></span>
              <span><small>Still in bag</small><strong>{summary.remaining}</strong></span>
              <span><small>Inferred used</small><strong>{summary.accountedConsumed}</strong></span>
              <span><small>Replacement added</small><strong>{summary.replacementQty}</strong></span>
              <span><small>Final quantity</small><strong>{summary.finalQty}</strong></span>
            </div>

            {group.oldLots.length === 0 ? (
              <div className="restock-reconcile-warning">
                <strong>No prior lot record for this item.</strong>
                <span>The reported {group.reportedUsed} used will be retained as untracked consumption. Scan the replacement product to establish the active inventory now.</span>
              </div>
            ) : (
              <div className="restock-reconcile-lots">
                <h4>1. Confirm what remains from the old inventory</h4>
                <p>You do not need the used vial or wrapper. Count what is physically still in the bag by recorded lot.</p>
                {group.oldLots.map(lot => (
                  <div className="restock-reconcile-lot" key={lot.recordId}>
                    <div>
                      <strong>{lot.lotSerial ? `Lot ${lot.lotSerial}` : 'Lot / serial not recorded'}</strong>
                      <small>{formatDate(lot.expirationDate)} · Previously recorded qty {lot.beforeQty}</small>
                    </div>
                    <label>Qty still in bag
                      <input
                        value={lot.remainingQty}
                        onChange={event => setRemaining(group.key, lot.recordId, event.target.value)}
                        inputMode="decimal"
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}

            <div className="restock-reconcile-add">
              <h4>2. Add replacement product</h4>
              <p>Scan the new product if convenient, then record the quantity and lot / expiration going into this bag.</p>
              <InventoryBarcodeScanner onScan={raw => scanReplacement(group, raw)} />
              {draft.barcode && <div className="restock-reconcile-barcode">Barcode captured: <strong>{draft.barcode}</strong></div>}
              <div className="restock-reconcile-form">
                <label>Quantity added
                  <input value={draft.quantity} onChange={event => setReplacementDraft(group.key, { quantity: event.target.value })} inputMode="decimal" />
                </label>
                <label>Lot / serial
                  <input value={draft.lotSerial} onChange={event => setReplacementDraft(group.key, { lotSerial: event.target.value })} placeholder="optional but recommended" />
                </label>
                <label>Expiration
                  <input type="date" value={draft.expirationDate} disabled={draft.noExpiration} onChange={event => setReplacementDraft(group.key, { expirationDate: event.target.value })} />
                </label>
                <label className="restock-reconcile-check"><input type="checkbox" checked={draft.noExpiration} onChange={event => setReplacementDraft(group.key, { noExpiration: event.target.checked, expirationDate: event.target.checked ? '' : draft.expirationDate })} /> No expiration / N/A</label>
              </div>
              <button type="button" className="inventory-primary-action" onClick={() => addReplacement(group)}>Add replacement lot</button>

              {group.replacements.length > 0 && (
                <div className="restock-reconcile-new-lots">
                  {group.replacements.map(line => (
                    <div key={line.id}>
                      <span><strong>Qty {line.quantity}</strong><small>{line.lotSerial ? `Lot ${line.lotSerial} · ` : ''}{line.noExpiration ? 'N/A expiration' : formatDate(line.expirationDate)}</small></span>
                      <button type="button" onClick={() => removeReplacement(group.key, line.id)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!groupCheck.valid && groupCheck.errors.length > 0 && (
              <div className="restock-reconcile-warning"><strong>Before this item can close:</strong><span>{groupCheck.errors[0]}</span></div>
            )}
          </article>
        );
      })}

      <section className="inventory-card restock-reconcile-final">
        <div>
          <h3>{validation.valid ? 'Reconciliation checks passed' : 'Finish the reconciliation above'}</h3>
          <p>{validation.valid
            ? 'Completing will update the bag inventory, retain the consumed lot history on this restock request, mark the request Restocked, and return non-damaged affected bags to Ready.'
            : validation.errors[0] || 'Confirm remaining quantities and add enough replacement product to restore each item to standard.'}</p>
        </div>
        <div className="restock-reconcile-actions">
          <button type="button" onClick={onCancel} disabled={status === 'saving'}>Cancel</button>
          <button type="button" className="inventory-primary-action" onClick={complete} disabled={!validation.valid || status === 'saving'}>
            {status === 'saving' ? 'Saving reconciliation…' : 'Complete Restock & Reconcile Inventory'}
          </button>
        </div>
      </section>
    </section>
  );
}
